import {
    Box,
    Flex,
    Icon,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import {
    TbAlertCircle,
    TbCalendar,
    TbCalendarEvent,
    TbCalendarWeek,
} from 'react-icons/tb';
import { IconType } from 'react-icons';
import { DeliveryBoardEtaFilter as EtaFilterValue } from '../hooks/useDeliveryBoardFeed';
import { DEFAULT_DELIVERY_BOARD_ETA_FILTERS } from '../hooks/useDeliveryBoardPreferences';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../ui';
import {
    CheckboxMenuSearch,
    MenuRow,
    MenuFooterActions,
    checkboxMenuColors,
    checkboxMenuTriggerMetrics,
} from '../ui/primitives/CheckboxMenu';

interface DeliveryBoardEtaFilterProps {
    filters: EtaFilterValue[];
    onChange: (filters: EtaFilterValue[]) => void;
}

const ETA_OPTIONS: { value: EtaFilterValue; label: string; icon: IconType }[] = [
    { value: 'today', label: 'Due Today', icon: TbCalendar },
    { value: 'tomorrow', label: 'Due Tomorrow', icon: TbCalendarEvent },
    { value: 'this-week', label: 'Due this Week', icon: TbCalendarWeek },
    { value: 'overdue', label: 'Overdue', icon: TbAlertCircle },
];

const isDefaultEtaFilters = (filters: EtaFilterValue[]) => (
    filters.length === DEFAULT_DELIVERY_BOARD_ETA_FILTERS.length
    && DEFAULT_DELIVERY_BOARD_ETA_FILTERS.every((value) => filters.includes(value))
);

const DeliveryBoardEtaFilter = ({ filters, onChange }: DeliveryBoardEtaFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState<EtaFilterValue[]>(filters);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const closePopover = () => {
        setIsOpen(false);
        setSearch('');
    };

    const openPopover = () => {
        setTempFilters(filters);
        setSearch('');
        setIsOpen(true);
    };

    const togglePopover = () => {
        if (isOpen) closePopover();
        else openPopover();
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-eta-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const toggleOption = (value: EtaFilterValue) => {
        setTempFilters((prev) => (
            prev.includes(value)
                ? prev.filter((entry) => entry !== value)
                : [...prev, value]
        ));
    };

    const commitFilters = (next: EtaFilterValue[]) => {
        onChange(next.length > 0 ? next : [...DEFAULT_DELIVERY_BOARD_ETA_FILTERS]);
    };

    const handleReset = () => {
        setTempFilters([...DEFAULT_DELIVERY_BOARD_ETA_FILTERS]);
    };

    const handleApply = () => {
        commitFilters(tempFilters);
        closePopover();
    };

    const handleRemove = (value: EtaFilterValue) => {
        commitFilters(filters.filter((entry) => entry !== value));
    };

    const handleClear = () => {
        commitFilters([...DEFAULT_DELIVERY_BOARD_ETA_FILTERS]);
        closePopover();
    };

    const filteredOptions = ETA_OPTIONS.filter((option) => {
        if (!search.trim()) return true;
        return option.label.toLowerCase().includes(search.toLowerCase().trim());
    });

    return (
        <Box position="relative" ref={containerRef}>
            <FilterBar alignContent="center">
                {filters.map((value) => {
                    const option = ETA_OPTIONS.find((entry) => entry.value === value);
                    if (!option) return null;
                    return (
                        <FilterChip
                            key={value}
                            icon={<Icon as={option.icon as React.ElementType} boxSize={4} />}
                            label={option.label}
                            onRemove={() => handleRemove(value)}
                            onClick={openPopover}
                        />
                    );
                })}

                <AddFilterMenu
                    isOpen={isOpen}
                    onToggle={togglePopover}
                    contentProps={{ 'data-eta-filter-popover': '' }}
                >
                            <CheckboxMenuSearch
                                value={search}
                                onChange={setSearch}
                                placeholder="Search filters..."
                            />

                            <VStack align="stretch" spacing={0} p="6px">
                                {filteredOptions.map((option) => (
                                    <MenuRow
                                        key={option.value}
                                        isSelected={tempFilters.includes(option.value)}
                                        icon={<Icon as={option.icon as React.ElementType} boxSize={4} />}
                                        onClick={() => toggleOption(option.value)}
                                    >
                                        {option.label}
                                    </MenuRow>
                                ))}
                                {filteredOptions.length === 0 && (
                                    <Flex px={3} py={3} justify="center">
                                        <Text
                                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                                            color={checkboxMenuColors.muted}
                                        >
                                            No matching filters
                                        </Text>
                                    </Flex>
                                )}
                            </VStack>

                            <MenuFooterActions onSecondary={handleReset} onApply={handleApply} />
                </AddFilterMenu>

                {!isDefaultEtaFilters(filters) && (
                    <ClearFiltersButton onClick={handleClear} />
                )}
            </FilterBar>
        </Box>
    );
};

export default DeliveryBoardEtaFilter;
